/**
 * Email HTML is untrusted (architecture §6.4, §9). It renders in an iframe whose `sandbox` never
 * has `allow-same-origin`: the frame has an opaque origin and cannot reach the app's origin,
 * cookies or storage. It has `allow-scripts` for one script only, ours (`HEIGHT_REPORTER`), which
 * tells the app the email's height so the frame can be as tall as the email; the CSP admits that
 * script by its hash and nothing else (no inline handlers, no `javascript:` URLs), and the email's
 * own scripts are stripped before rendering as well. The `srcdoc`'s first element is that strict
 * CSP: nothing loads except inline styles, `data:` images and images from the api's origin, where
 * the api's image proxy serves the email's remote images (it rewrote them and removed tracking
 * pixels, architecture §6.4), so no request ever reaches the sender. Forms cannot post and links
 * open in a new tab.
 */
export const IFRAME_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

/**
 * Posts the email's height to the app whenever it changes; the only script the frame runs. It
 * measures the root element's box, not `scrollHeight`, which never reports less than the frame
 * itself and so would keep a short email at the frame's starting height.
 */
export const HEIGHT_REPORTER =
  '(function(){var last=0;function send(){var h=Math.ceil(document.documentElement.getBoundingClientRect().height);if(h!==last){last=h;parent.postMessage({kwpFrameHeight:h},"*");}}var ro=new ResizeObserver(send);ro.observe(document.documentElement);ro.observe(document.body);addEventListener("load",send);send();})();';

/** sha256 of `HEIGHT_REPORTER`, base64 (a test recomputes it, so the two cannot drift apart). */
export const HEIGHT_REPORTER_HASH = "sha256-b9uV60u87pQNcnvmHIFVnbAUS9fR5sYyQ2jHreAcXjQ=";

/** The height a frame reported in a `message` event, or null when the event is not one. */
export function reportedHeight(data: unknown): number | null {
  if (typeof data !== "object" || data === null) return null;
  const h = (data as Record<string, unknown>)["kwpFrameHeight"];
  if (typeof h !== "number" || !Number.isFinite(h) || h < 0) return null;
  return Math.min(Math.ceil(h), MAX_FRAME_HEIGHT);
}

/** A ceiling on a reported height, so a broken page cannot make the frame absurdly tall. */
export const MAX_FRAME_HEIGHT = 50_000;

export function contentSecurityPolicy(imageOrigin: string | null): string {
  return [
    "default-src 'none'",
    `script-src '${HEIGHT_REPORTER_HASH}'`,
    `img-src data:${imageOrigin === null ? "" : ` ${imageOrigin}`}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
    "form-action 'none'",
  ].join("; ");
}

/**
 * Elements removed before rendering even though the CSP and sandbox already neuter them: scripts
 * (belt and braces), `<meta>` (a refresh would navigate the frame), `<base>`, `<link>`.
 */
const STRIP = [
  /<script\b[\s\S]*?<\/script\s*>/gi,
  /<script\b[^>]*>/gi,
  /<meta\b[^>]*>/gi,
  /<base\b[^>]*>/gi,
  /<link\b[^>]*>/gi,
];

export function stripActiveContent(html: string): string {
  return STRIP.reduce((out, re) => out.replace(re, ""), html);
}

const BASE_STYLE =
  "html{color-scheme:light}body{margin:12px;font:14px/1.5 ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;color:#1d1d1b;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}";

export function buildSrcdoc(html: string, options: { imageOrigin: string | null }): string {
  return [
    "<!doctype html><html><head>",
    `<meta http-equiv="Content-Security-Policy" content="${contentSecurityPolicy(options.imageOrigin)}">`,
    '<meta charset="utf-8"><meta name="referrer" content="no-referrer"><base target="_blank">',
    `<style>${BASE_STYLE}</style>`,
    "</head><body>",
    stripActiveContent(html),
    `<script>${HEIGHT_REPORTER}</script>`,
    "</body></html>",
  ].join("");
}
